import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { api } from "../../../api/apiService";
import { Link, useNavigate } from "react-router-dom";
import ReactPaginate from "react-paginate";
import { Spinner } from "react-bootstrap";
import Select from "react-select";
import moment from "moment";
import AttendanceModal from "../../../components/Modal/AddAttendanceModal";
import ConfirmationModal from "../../../components/Modal/ConfimationModal";
import RegisterIdentityModal from "../../../components/Modal/RegisterIdentityModal";
import { InitialAvatar, formatEmail, getImageUrl } from "../../../config/utils";
import DeleteModal from "../../../components/Modal/DeleteModal";
import EmployeeFormModal from "../../../components/Modal/EmployeeFormModal";

const Employee = () => {
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [branches, setBranches] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [roles, setRoles] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentEmployee, setCurrentEmployee] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loader, setLoader] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(null);

  const [image, setImage] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [onHover, setOnHover] = useState(false);
  const [fileInputKey, setFileInputKey] = useState(Date.now());
  const [selectedDepartment, setSelectedDepartment] = useState("");
  const [selectedShift, setSelectedShift] = useState("");

  const [attendanceModal, setAttendanceModal] = useState(false);
  // const [confirmatioModal, setConfirmationModal] = useState(false);
  const [activeModal, setActiveModal] = useState(null);
  const [identityModal, setIdentityModal] = useState(false);

  useEffect(() => {
    fetchEmployeeCreate();
  }, []);

  const fetchEmployees = async (
    page,
    search = "",
    department = "",
    shift = ""
  ) => {
    try {
      setIsLoading(true);

      // Build the URL with proper query parameters
      const queryParams = new URLSearchParams();
      queryParams.append("page", page);
      if (search) {
        queryParams.append("search", search);
      }
      if (department) queryParams.append("department", department);
      if (shift) queryParams.append("shift", shift);
      const response = await api.get(`/employees?${queryParams.toString()}`);
      const { data, last_page, total } = response.data.data;

      setEmployees(response.data.data.data);
      setTotalPages(last_page);
      setTotal(total);
    } catch (error) {
      toast.error("Failed to fetch employees");
    } finally {
      setIsLoading(false);
    }
  };

  const handlePageChange = (selectedItem) => {
    const newPage = selectedItem.selected + 1;
    setCurrentPage(newPage);
  };

  const handleSearch = () => {
    setCurrentPage(1);
    fetchEmployees(1, searchTerm, selectedDepartment, selectedShift);
  };

  useEffect(() => {
    fetchEmployees(currentPage, searchTerm, selectedDepartment, selectedShift);
  }, [currentPage]);

  const clearSearch = () => {
    setSearchTerm("");
    setSelectedDepartment("");
    setSelectedShift("");
    setCurrentPage(1);
    fetchEmployees(1, "", "", "");
  };

  const fetchEmployeeCreate = async () => {
    try {
      const response = await api.get("/employee/create");
      setShifts(response.data.data.shifts);
      setDepartments(response.data.data.departments);
      setBranches(response.data.data.branches);
      setRoles(response.data.data.roles);
    } catch (error) {
      console.error("Error fetching Employee: ", error);
      toast.error("Failed to fetch Employee");
    }
  };

  const handleAddEmployee = () => {
    setCurrentEmployee({
      profile_picture: "",
      first_name: "",
      last_name: "",
      username: "",
      email: "",
      phone_number: "",
      department_id: "",
      branch_id: "",
      role_id: "",
      fingerprint_id: "",
      monthly_salary: "",
      password: "",
      auto_approve: 0,
      enable_notification: 0,
      status: "active",
    });
    setIsEditing(false);
    setShowModal(true);
  };

  const handleEditEmployee = (employee) => {
    setPreviewUrl(
      `${process.env.REACT_APP_API_BASE_URL}/uploads/${employee?.profile_picture}` ||
        null
    );
    setIsEditing(true);
    setShowModal(true);
    employeeData(employee?.employee_id);
  };

  const employeeData = async (employeeId) => {
    try {
      setLoader(true);
      const res = await api.get(`/employees/${employeeId}`);
      setCurrentEmployee(res.data);
    } catch (error) {
      console.error("Error fetching employee data:", error);
    } finally {
      setLoader(false);
    }
  };

  const handleRegisterIdentity = (employee) => {
    setCurrentEmployee(employee);
    setIdentityModal(true);
  };

  const handleAddAttendance = (employee) => {
    setCurrentEmployee(employee);
    setAttendanceModal(true);
  };

  const handleDeleteEmployee = async () => {
    if (!currentEmployee) return;
    try {
      setLoader(true);
      await api.delete(`/employees/${currentEmployee?.employee_id}`);
      setEmployees((prev) =>
        prev.filter((emp) => emp.employee_id !== currentEmployee?.employee_id)
      );
      setCurrentPage(1);
      setTotal((prev) => prev - 1);
      toast.success("Employee deleted successfully");
    } catch (error) {
      if (error.data && error.data.errors) {
        error.data.errors.forEach((err) => {
          toast.error(err);
        });
      } else {
        toast.error("Error deleting employee");
      }
    } finally {
      setActiveModal(null);
      setLoader(false);
    }
  };

  const handlePrintPage = (id) => {
    window.open(`/user/employee/print?id=${id}`, "_blank");
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImage(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const getInitials = (name) => {
    if (!name) return "";
    const nameParts = name.split(" ");
    const initials = nameParts
      .map((part) => part.charAt(0).toUpperCase())
      .join("");
    return initials;
  };

  const saveEmployee = async (data) => {
    setIsLoading(true);

    try {
      const formData = new FormData();

      const formattedData = {
        ...data,
        enable_notification: data.enable_notification ? 1 : 0,
        auto_approve: data.auto_approve ? 1 : 0,
      };

      // Append formatted form fields to FormData
      Object.keys(formattedData).forEach((key) => {
        formData.append(key, formattedData[key]);
      });

      // Append the image file if it exists
      if (image) {
        formData.append("profile_picture", image);
      }

      if (isEditing && currentEmployee) {
        const employeeId = currentEmployee.employee_id;

        if (!employeeId) {
          throw new Error("Employee ID is undefined");
        }

        // Add _method=PUT only for updates
        formData.append("_method", "PUT");

        // Use POST with _method=PUT for updates
        await api.post(`/employees/${employeeId}`, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
        toast.success("Employee updated successfully");
      } else {
        // Regular POST for creating new employee
        await api.post("/employees", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
        toast.success("Employee added successfully");
      }

      fetchEmployees();
      setShowModal(false);
      setPreviewUrl("");
      // reset();
    } catch (error) {
      if (error.data && error.data.errors) {
        error.data.errors.forEach((err) => {
          toast.error(err);
        });
      } else {
        toast.error("Error saving employee");
      }
    } finally {
      setIsLoading(false);
      setCurrentEmployee(null);
    }
  };

  const handleSendDevice = async () => {
    if (!currentEmployee) return;
    try {
      setLoader(true);
      // await api.post();
      console.log("data");
    } catch (error) {
      console.error("Error sending device:", error);
    } finally {
      setActiveModal(null);
      setLoader(false);
    }
  };

  return (
    <main id="main" className="main">
      <div className="pagetitle">
        <h1>Employees</h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/home">Home</Link>
            </li>
            <li className="breadcrumb-item active">Employees</li>
          </ol>
        </nav>
      </div>

      <section className="section">
        <div className="row">
          <div className="col-lg-12">
            <div className="card">
              <div className="card-body">
                <h5 className="card-title">Employee Management</h5>
                <div>
                  <button
                    className="btn btn-primary mb-3 me-3"
                    onClick={handleAddEmployee}
                  >
                    Add Employee
                  </button>
                  <button
                    className="btn btn-primary mb-3"
                    onClick={() => {
                      navigate("/user/bulk-employees");
                    }}
                  >
                    Add Bulk Employees
                  </button>

                  <div className="row mb-3 g-2">
                    <div className="col-12 col-sm-6 col-md-3">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Search name, email or contact"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </div>
                    <div className="col-12 col-sm-6 col-md-3">
                      <Select
                        className="basic-single"
                        classNamePrefix="select"
                        value={
                          selectedDepartment
                            ? {
                                value: selectedDepartment,
                                label: selectedDepartment,
                              }
                            : null
                        }
                        onChange={(selectedOption) =>
                          setSelectedDepartment(
                            selectedOption ? selectedOption.value : ""
                          )
                        }
                        options={departments.map((dept) => ({
                          value: dept.department_name,
                          label: dept.department_name,
                        }))}
                        placeholder="All Departments"
                        isSearchable
                        isClearable
                        noOptionsMessage={() => "No options"}
                        styles={{
                          control: (provided) => ({
                            ...provided,
                            borderRadius: "6px",
                          }),
                          placeholder: (provided) => ({
                            ...provided,
                            whiteSpace: "nowrap",
                          }),
                        }}
                      />
                    </div>
                    <div className="col-12 col-sm-6 col-md-3">
                      <Select
                        className="basic-single"
                        classNamePrefix="select"
                        value={
                          selectedShift
                            ? { value: selectedShift, label: selectedShift }
                            : null
                        }
                        onChange={(selectedOption) =>
                          setSelectedShift(
                            selectedOption ? selectedOption.label : ""
                          )
                        }
                        options={shifts.map((shift) => ({
                          value: shift.shift_name,
                          label: shift.shift_name,
                        }))}
                        placeholder="All Shifts"
                        isSearchable
                        isClearable
                        noOptionsMessage={() => "No options"}
                        styles={{
                          control: (provided) => ({
                            ...provided,
                            borderRadius: "6px",
                          }),
                          placeholder: (provided) => ({
                            ...provided,
                            whiteSpace: "nowrap",
                          }),
                        }}
                      />
                    </div>
                    <div className="col-12 col-sm-6 col-md-3">
                      <button
                        className="btn btn-primary me-2"
                        onClick={handleSearch}
                      >
                        Search
                      </button>
                      <button
                        className="btn btn-secondary"
                        onClick={clearSearch}
                      >
                        Clear
                      </button>
                    </div>
                  </div>

                  {isLoading ? (
                    <div className="text-center mt-3">
                      <Spinner animation="border" variant="primary" />
                      <span className="ms-2">Loading...</span>
                    </div>
                  ) : (
                    <>
                      <div className="table-responsive">
                        <div className="mb-4 d-flex align-items-center">
                          <strong>No. of Employee</strong>
                          <span className="ms-2 badge badge-success">
                            {total}
                          </span>
                        </div>

                        <table className="table">
                          <thead>
                            <tr>
                              <th>Name</th>
                              <th>Email/Contact</th>
                              <th>Shift</th>
                              <th>Department</th>
                              <th>Status</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {employees?.length > 0 ? (
                              employees.map((employee, index) => (
                                <tr key={employee.employee_id || index}>
                                  <td>
                                    <div className="d-flex align-items-center gap-2">
                                      {employee?.profile_picture ? (
                                        <img
                                          src={getImageUrl(
                                            employee.profile_picture
                                          )}
                                          alt={employee.employee_name}
                                          style={{
                                            width: "30px",
                                            height: "30px",
                                            borderRadius: "50%",
                                            objectFit: "cover",
                                          }}
                                        />
                                      ) : (
                                        <InitialAvatar
                                          name={`${employee.first_name} ${employee.last_name}`}
                                        />
                                      )}
                                      <div>
                                        <a
                                          style={{ color: "black" }}
                                          href={`/user/report/${employee.employee_id}`}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                        >
                                          {`${employee.first_name} ${employee.last_name}`}
                                        </a>
                                        <p className="m-0 sequence-number text-primary">
                                          ID: #{employee?.sequence_number}
                                        </p>
                                        <p className="m-0 small">
                                          <span className="me-1">
                                            Last seen:
                                          </span>
                                          {employee?.last_seen
                                            ? moment(
                                                employee?.last_seen
                                              ).format("DD-MM-YY hh:mm A")
                                            : "---"}
                                        </p>
                                      </div>
                                    </div>
                                  </td>
                                  <td
                                    className="c-pointer"
                                    title={employee.email || ""}
                                  >
                                    {formatEmail(employee?.email)}
                                    <br />
                                    {employee.phone_number || "—"}
                                  </td>
                                  <td>
                                    {employee?.shift ? (
                                      <>
                                        {employee.shift.shift_name}
                                        <p>
                                          {moment(
                                            employee.shift.start_time,
                                            "HH:mm:ss"
                                          ).format("h:mm A")}{" "}
                                          -{" "}
                                          {moment(
                                            employee.shift.end_time,
                                            "HH:mm:ss"
                                          ).format("h:mm A")}
                                        </p>
                                      </>
                                    ) : (
                                      "null"
                                    )}
                                  </td>
                                  <td>
                                    {employee.department?.department_name}
                                    <p className="m-0 small">
                                      ({employee.role?.role_name})
                                    </p>
                                  </td>
                                  <td>
                                    {employee.status === "active" ? (
                                      <span className="badge bg-success">
                                        Active
                                      </span>
                                    ) : (
                                      <span className="badge bg-danger">
                                        Inactive
                                      </span>
                                    )}
                                  </td>
                                  <td>
                                    {/* <button
                                      className={`btn btn-sm me-2 mb-1 ${
                                        employee.identity
                                          ? "btn-success"
                                          : "btn-primary"
                                      }`}
                                      onClick={() =>
                                        handleRegisterIdentity(employee)
                                      }
                                      data-bs-toggle="tooltip"
                                      data-bs-placement="top"
                                      title="Register Identity"
                                    >
                                      <i className="bi bi-person-plus"></i>
                                    </button>
                                    <button
                                      className="btn btn-info btn-sm me-2 mb-1"
                                      onClick={() =>
                                        handleAddAttendance(employee)
                                      }
                                      data-bs-toggle="tooltip"
                                      data-bs-placement="top"
                                      title="Add Attendance"
                                    >
                                      <i className="bi bi-plus-circle"></i>
                                    </button> */}
                                    <button
                                      className="btn btn-warning btn-sm me-2 mb-1"
                                      onClick={() =>
                                        handleEditEmployee(employee)
                                      }
                                      data-bs-toggle="tooltip"
                                      data-bs-placement="top"
                                      title="Edit Employee"
                                    >
                                      <i className="bi bi-pencil-square"></i>
                                    </button>
                                    <button
                                      className="btn btn-danger btn-sm me-2 mb-1"
                                      onClick={() => {
                                        setCurrentEmployee(employee);
                                        setActiveModal("delete");
                                      }}
                                      data-bs-toggle="tooltip"
                                      data-bs-placement="top"
                                      title="Delete Employee"
                                    >
                                      <i className="bi bi-trash"></i>
                                    </button>
                                    <button
                                      className="btn btn-warning btn-sm mb-1"
                                      onClick={() =>
                                        handlePrintPage(employee?.employee_id)
                                      }
                                      data-bs-toggle="tooltip"
                                      data-bs-placement="top"
                                      title="Print Employee"
                                    >
                                      <i className="bi bi-printer"></i>
                                    </button>
                                    <button
                                      className="btn btn-secondary btn-sm mb-1 ms-2"
                                      onClick={() => {
                                        setCurrentEmployee(employee);
                                        setActiveModal("device");
                                      }}
                                      data-bs-toggle="tooltip"
                                      data-bs-placement="top"
                                      title="Send to Device"
                                      disabled={!!employee?.last_seen}
                                    >
                                      <i className="bi bi-box-arrow-up"></i>
                                    </button>
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan="6" className="text-center">
                                  No employees found.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>

                      {totalPages > 1 && (
                        <ReactPaginate
                          previousLabel="Prev"
                          nextLabel="Next"
                          breakLabel="..."
                          pageCount={totalPages}
                          marginPagesDisplayed={2}
                          pageRangeDisplayed={5}
                          onPageChange={handlePageChange}
                          containerClassName="pagination"
                          activeClassName="active"
                          disabledClassName="disabled"
                          forcePage={currentPage - 1}
                        />
                      )}
                    </>
                  )}

                  {/* add/edit employee modal*/}

                  {showModal && (
                    <EmployeeFormModal
                      isEditing={isEditing}
                      isLoading={isLoading}
                      show={showModal}
                      onClose={() => {
                        setShowModal(false);
                        setPreviewUrl("");
                        setCurrentEmployee(null);
                      }}
                      onMouseEnter={() => setOnHover(true)}
                      onMouseLeave={() => setOnHover(false)}
                      onSubmit={saveEmployee}
                      previewUrl={previewUrl}
                      fileInputKey={fileInputKey}
                      imageChange={handleImageChange}
                      getInitials={getInitials}
                      onHover={onHover}
                      loader={loader}
                      currentEmployee={currentEmployee}
                      roles={roles}
                      departments={departments}
                      shifts={shifts}
                      Spinner={Spinner}
                      branches={branches}
                      defaultValues={{
                        first_name: currentEmployee?.first_name || "",
                        last_name: currentEmployee?.last_name || "",
                        username: currentEmployee?.username || "",
                        email: currentEmployee?.email || "",
                        phone_number: currentEmployee?.phone_number || "",
                        department_id: currentEmployee?.department_id || "",
                        branch_id: currentEmployee?.branch_id || "",
                        role_id: currentEmployee?.role_id || "",
                        shift_id: currentEmployee?.shift_id || "",
                        monthly_salary: currentEmployee?.monthly_salary || "",
                        password: currentEmployee?.password || "",
                        fingerprint_id: currentEmployee?.fingerprint_id || "",
                        enable_notification:
                          currentEmployee?.enable_notification || "",
                        auto_approve: currentEmployee?.auto_approve || "",
                        status: currentEmployee?.status || "active",
                      }}
                    />
                  )}

                  {identityModal && (
                    <RegisterIdentityModal
                      show={identityModal}
                      onClose={() => setIdentityModal(false)}
                      currentEmployee={currentEmployee}
                    />
                  )}

                  {attendanceModal && (
                    <AttendanceModal
                      show={attendanceModal}
                      onClose={() => setAttendanceModal(false)}
                      currentEmployee={currentEmployee}
                    />
                  )}

                  {activeModal === "device" && (
                    <ConfirmationModal
                      show={true}
                      onClose={() => setActiveModal(null)}
                      onDevice={handleSendDevice}
                      isLoading={loader}
                      body="Are you sure you want to send this employee to the device?"
                    />
                  )}
                  {activeModal === "delete" && (
                    <DeleteModal
                      show={true}
                      onClose={() => setActiveModal(null)}
                      onDelete={handleDeleteEmployee}
                      isLoading={loader}
                      body={
                        <>
                          Are you sure you want to delete employee{" "}
                          <strong className="text-capitalize">
                            "{currentEmployee?.first_name}{" "}
                            {currentEmployee?.last_name}"
                          </strong>
                          ?
                        </>
                      }
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Employee;
